import { Field, FieldProps, Formik, FormikProps } from 'formik';
import React, { useMemo, useCallback, useState, useRef, ChangeEvent, KeyboardEvent, useEffect } from 'react';
import { object as objectYup, string as stringYup, ref as refYup } from 'yup';
import websitelogo from '../../../assets/img/offerx-logo.svg';
import getValue from 'lodash/get';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import useInitialErrors from '../../hooks/formik-initial-errors';
import { RootState } from '../../redux';
import { OTPType, OtpValidationType, validateOTPFormType } from '../../types/userOtp';
import { userOtpService } from '../../lib/api/userOtpValidate';
import { getCountryDetailsByIPAddress, setLoading } from '../../redux/policy';
import { emailRegexp, passwordRegExp } from '../../lib/utils/validation';
import { ForgotPasswordType } from '../../types/customAuth';
import { customAuthService } from '../../lib/api/customAuth';
import useUser, { useHRMSUser, usePolicy } from '../../hooks/useSelector';
import { Message, SweetAlertTitle, WarningIcon } from '../../lib/constants/messages';
import { history } from '../../history';
import { SocialEnum } from '../../types/auth';
import queryString from 'query-string';
import { localStorageGetItem, localStorageSetItem } from '../storage/localStorageManager';
import { CandidateStatus } from '../../types/claimed';
import { useAppDispatch } from '../../redux/reduxhooks';

type Props = {
    emailLabel?: string;
    otpType: OTPType;
    emailDisabled?: boolean;
    clearOTPValue?: boolean;
    emailPlaceholder?: string;
    emailValue?: string;
    setEmailValue?: (value: string) => void;
    setLoadingSpin?: (value: boolean) => void;
    verifyLabel?: string;
    verifyPlaceholder?: string;
    verifyDisabled?: boolean;
    verifyValue?: string;
    hideVerificationInput?: boolean;
    setHideVerificationInput?: (key: boolean) => void;
    setShowPasswordTab?: (key: boolean) => void;
    setVerifyValue?: (value: string) => void;
    candidateType?: string;
    setPageLoading?: (key: boolean) => void;
    handleSelect?: (key: string, value: string) => void;
    setShowSelectAccountType?: (key: boolean) => void;
    setNewEmail?: (key: string) => void;
    setMigrateFromEmailOnly?: (key: boolean) => void;
    setAuthType: (key: string) => void;
    setActiveTab?: (key: string) => void;
    useLargeButtons?: boolean;
    disableGetOTPButton?: boolean;
};

const initialFormData = {
    email: '',
    otp: '',
    password: '',
    cpassword: '',
};

const NewRegOTP: React.FC<Props> = ({
    candidateType,
    emailDisabled,
    emailLabel,
    emailPlaceholder,
    setMigrateFromEmailOnly,
    otpType,
    useLargeButtons,
    verifyDisabled,
    clearOTPValue,
    verifyLabel,
    hideVerificationInput,
    setHideVerificationInput,
    setLoadingSpin,
    setShowPasswordTab,
    setAuthType,
    disableGetOTPButton,
    setActiveTab,
}) => {
    const [isOTPNotValidated, setIsOTPNotValidated] = useState(false);
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const otpFields = useRef<HTMLInputElement[]>([]);
    const [concatenatedOtp, setConcatenatedOtp] = useState(clearOTPValue ? '' : '');
    const [removeSentMessage, setRemoveSentMessage] = useState(false);
    const [disableResetButton, setDisableResetButton] = useState(false);
    const [timer, setTimer] = useState<number>(30);
    const [enableNewRegOTPBtn, setEnableNewRegOTPBtn] = useState(true);
    const [otpExpired, setOtpExpired] = useState(false);
    const [showOTPBtn, setShowOTPBtn] = useState(true);
    const [otpVerified, setOtpVerified] = useState(false);
    const [hideAfterVerifiedText, setHideAfterVerifiedText] = useState(false);
    const [showSpin, setShowSpin] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState('');

    const { ConfirmCandidateInvitationId, ConfirmManagerInvitationId } = queryString.parse(history.location.search);

    const dispatch = useAppDispatch();
    const policy = usePolicy();
    const hasUser = useUser();
    const hasHRMSUser = useHRMSUser();

    const profileFormData = useMemo(() => {}, []);

    const affilateReferral = localStorageGetItem('referral', true);

    const unclaimedCandidateData = localStorageGetItem('seosearch', false);
    const candidateStatus = unclaimedCandidateData?.data?.candidateStatus;
    const candidateId = unclaimedCandidateData?.data?.id;
    const initialErrors = useInitialErrors(profileFormData, getValidationSchema());

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const sanitizedValue = e.target.value.replace(/[^0-9]/g, '');
        const newOtp = [...otp];
        newOtp[index] = sanitizedValue;
        setOtp(newOtp);

        if (index < 5 && sanitizedValue !== '') {
            otpFields.current[index + 1].focus();
            setOtpVerified(false);
        }

        const newFilledFields = newOtp.map((digit, i) => (digit ? i : -1)).filter((digit) => digit !== -1);
        // setFilledFields(newFilledFields);

        const concatenatedValue = newOtp.join('');
        setConcatenatedOtp(concatenatedValue);

        const anyFieldFilled = newFilledFields.length > 0;

        if (anyFieldFilled) {
            setRemoveSentMessage?.(false);
        }
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
            otpFields.current[index - 1].focus();
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
            setOtpExpired(false);
            setIsOTPNotValidated(false);
        }
    };

    const validateOtp = async (data: validateOTPFormType) => {
        try {
            setLoadingSpin?.(true);

            const path = history.location.pathname;

            const {
                resultObject: { otpValidateType },
            } =
                path !== '/api-integrate'
                    ? await userOtpService.validateOTP(data)
                    : await userOtpService.validateHRMSOTP(data);

            if (otpValidateType === OtpValidationType.Valid) {
                setOtpVerified(true);
                setLoadingSpin?.(false);
                setHideAfterVerifiedText?.(true);
                setAuthType('customAuth');
                setActiveTab?.('customAuth');
            } else if (otpValidateType === OtpValidationType.InValid) {
                setOtpVerified(false);
                setIsOTPNotValidated(true);
                setRemoveSentMessage(false);
                setLoadingSpin?.(false);
            } else if (otpValidateType === OtpValidationType.Expired) {
                setOtpExpired(true);
                setLoadingSpin?.(false);
            } else {
                setOtpVerified(false);
                setLoadingSpin?.(false);
            }
        } catch (err) {
            console.log(err, 'OTP Error');
            setLoadingSpin?.(false);
        }
    };

    const resetOtp = () => {
        setRemoveSentMessage?.(true);
        setOtpExpired(false);
        setIsOTPNotValidated(false);
        setOtp(Array(6).fill(''));
        setConcatenatedOtp('');
        setOtpVerified(false);
    };

    const fetchData = async (emailId: string, otpType: OTPType, candidateId?: string) => {
        try {
            setLoadingSpin?.(true);
            setShowSpin(true);
            setEnableNewRegOTPBtn(true);

            const path = history.location.pathname;
            const affilateReferral = localStorageGetItem('referral', true);

            const {
                resultObject: { otpValidateType, email, isEmailInvalid },
            } =
                path !== '/api-integrate'
                    ? await userOtpService.updateUserOtp({
                          email: emailId,
                          otpType: affilateReferral ? OTPType.AffiliateMarketing : otpType,
                          candidateId,
                      })
                    : await userOtpService.updateHRMSUserOtp({ email: emailId, otpType });

            if (isEmailInvalid) {
                setMaskedEmail(email);
            }
            if (otpValidateType === OtpValidationType.Valid) {
                setRemoveSentMessage(true);
                setShowOTPBtn(false);
                setLoadingSpin?.(false);
                // setHideVerificationInput?.(false);
            } else if (otpValidateType === OtpValidationType.InValid && !candidateId) {
                setLoadingSpin?.(false);
                Swal.fire({
                    title: SweetAlertTitle.Empty,
                    icon: WarningIcon,
                    text: Message.EmailAlreadyRegistered,
                });
            }
            setLoadingSpin?.(false);
            setEnableNewRegOTPBtn(false);
            setShowSpin(false);
        } catch (err) {
            // setError('An error occurred while fetching data.');
            setLoadingSpin?.(false);
            setShowSpin(false);
        }
    };

    useEffect(() => {
        if (disableResetButton) {
            setTimeout(() => {
                setDisableResetButton(false);
            }, 30000);
        }
    }, [disableResetButton]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const decrementTimer = () => {
            setTimer((prevTimer) => prevTimer - 1);
        };

        intervalId = setInterval(decrementTimer, 1000);

        if (timer === 0) {
            clearInterval(intervalId);
            setDisableResetButton(false);
        }

        return () => {
            clearInterval(intervalId);
        };
    }, [timer]);

    useEffect(() => {
        if (otpVerified) {
            setIsOTPNotValidated(false);
            setRemoveSentMessage(false);
            setOtpExpired(false);
        }
    }, [otpVerified]);

    useMemo(() => {
        async function fetchData() {
            try {
                dispatch(setLoading(true));

                dispatch(getCountryDetailsByIPAddress());
            } catch (error) {
                console.error('Error:', error);
            } finally {
                dispatch(setLoading(false));
            }
        }

        fetchData();
    }, [dispatch]);

    return (
        <>
            {' '}
            <Formik
                onSubmit={() => {}}
                enableReinitialize
                initialValues={initialFormData}
                validationSchema={getValidationSchema()}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<any>) => {
                    const { handleSubmit, values, isSubmitting, resetForm, setFieldTouched, setFieldValue } =
                        formikProps;

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-12">
                                    <label className="fs-14 fw-400 mb-2 ">
                                        {emailLabel} <span className="lt-text-error">*</span>
                                    </label>

                                    <Field name="email">
                                        {(fieldProps: FieldProps) => {
                                            const { field, form } = fieldProps;
                                            const error =
                                                getValue(form.touched, field.name) && getValue(form.errors, field.name);

                                            if (values.email !== '' && !error) {
                                                setEnableNewRegOTPBtn(false);
                                            } else {
                                                setEnableNewRegOTPBtn(true);
                                            }
                                            return (
                                                <>
                                                    <div className="position-relative">
                                                        <input
                                                            type="email"
                                                            disabled={emailDisabled ? emailDisabled : !showOTPBtn}
                                                            className="form-control"
                                                            value={values.email}
                                                            placeholder={emailPlaceholder}
                                                            onChange={(ev) => {
                                                                setFieldTouched(field.name);
                                                                setMaskedEmail('');
                                                                setFieldValue(field.name, ev.target.value);
                                                            }}
                                                            // disabled
                                                        />
                                                        {otpVerified && (
                                                            <i className="bi bi-check-circle-fill lt-text-success email-check"></i>
                                                        )}
                                                    </div>

                                                    {typeof error === 'string' && (
                                                        <small className="text-danger fs-12">{error}</small>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </Field>
                                    {maskedEmail && (
                                        <div className="lt-text-error fs-12 mt-2">
                                            {`Please use correct email to enter as per the hint! ${maskedEmail}`}
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center text-end mt-1 mb-2">
                                        <div className="">
                                            {!otpVerified ? (
                                                <>
                                                    {!otpExpired && (
                                                        <>
                                                            {!isOTPNotValidated ? (
                                                                <small
                                                                    className="text-success fs-12"
                                                                    style={{
                                                                        display: `${
                                                                            removeSentMessage && !otpExpired
                                                                                ? 'block'
                                                                                : 'none'
                                                                        }`,
                                                                    }}
                                                                >
                                                                    An OTP has been sent to the email.
                                                                </small>
                                                            ) : (
                                                                ''
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <small
                                                    className="text-success fs-12"
                                                    style={{
                                                        display: `${otpVerified ? 'block' : 'none'}`,
                                                    }}
                                                >
                                                    Email verified.
                                                </small>
                                            )}
                                        </div>
                                        <div>
                                            {!hideAfterVerifiedText &&
                                                (showOTPBtn ? (
                                                    <button
                                                        className="btn btn-primary px-4 mt-2"
                                                        type="button"
                                                        disabled={enableNewRegOTPBtn || showSpin || disableGetOTPButton}
                                                        onClick={() => {
                                                            if ((hasUser && hasUser?.email) === values.email) {
                                                                Swal.fire({
                                                                    icon: WarningIcon,
                                                                    text: Message.ExistingAndNewEmail,
                                                                });
                                                            } else {
                                                                if (
                                                                    candidateStatus !== undefined &&
                                                                    candidateStatus === CandidateStatus.Uploaded && // assuming `CandidateStatus.Joined === 1`
                                                                    candidateId
                                                                ) {
                                                                    fetchData(
                                                                        values.email,
                                                                        OTPType.Registration,
                                                                        candidateId
                                                                    );
                                                                } else {
                                                                    fetchData(values.email, otpType);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {showSpin && (
                                                            <span className="spinner-border spinner-border-sm me-2" />
                                                        )}
                                                        Get OTP
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            setOtp(Array(6).fill(''));
                                                            setFieldValue('email', '');
                                                            setFieldValue('otp', '');
                                                            setRemoveSentMessage(false);
                                                            setShowOTPBtn(true);
                                                            setOtpVerified(false);
                                                            setIsOTPNotValidated(false);
                                                            setOtpExpired(false);
                                                            setTimer(0);
                                                            setHideVerificationInput?.(true);
                                                        }}
                                                        type="button"
                                                        className="fs-12 text-decoration-underline lt-text-primary-alt cursor-pointer"
                                                    >
                                                        Change Email
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                                {!hideVerificationInput && !showOTPBtn && (
                                    <div className="col-md-12">
                                        <div className="mb-4">
                                            <label className="fs-14 fw-400 mb-2">{verifyLabel}</label>
                                            <Field name="otp">
                                                {(fieldProps: FieldProps) => {
                                                    const { field, form } = fieldProps;
                                                    const error =
                                                        getValue(form.touched, field.name) &&
                                                        getValue(form.errors, field.name);

                                                    return (
                                                        <>
                                                            <div className="fs-14">
                                                                Verification Code{' '}
                                                                <span className="lt-text-error">*</span>
                                                            </div>
                                                            <div className="row mb-3 mt-2">
                                                                {otp.map((digit, index) => (
                                                                    <div className="col-lg-2 col-2">
                                                                        <input
                                                                            key={index}
                                                                            type="text"
                                                                            pattern="[0-9]"
                                                                            className="form-control px-1 text-center"
                                                                            maxLength={1}
                                                                            disabled={verifyDisabled}
                                                                            value={digit}
                                                                            onChange={(e) =>
                                                                                handleInputChange(e, index)
                                                                            }
                                                                            onKeyDown={(e) =>
                                                                                handleInputKeyDown(e, index)
                                                                            }
                                                                            ref={(input) =>
                                                                                (otpFields.current[index] =
                                                                                    input as HTMLInputElement)
                                                                            }
                                                                            title="OTP"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {typeof error === 'string' && (
                                                                <small className="text-danger fs-12">{error}</small>
                                                            )}
                                                        </>
                                                    );
                                                }}
                                            </Field>
                                            <div className="d-flex justify-content-between m-1">
                                                <div>
                                                    {!otpExpired ? (
                                                        <>
                                                            <small
                                                                className="text-danger fs-12"
                                                                style={{
                                                                    display: `${
                                                                        isOTPNotValidated && !otpExpired
                                                                            ? 'block'
                                                                            : 'none'
                                                                    }`,
                                                                }}
                                                            >
                                                                Invalid OTP.
                                                            </small>
                                                        </>
                                                    ) : (
                                                        <small
                                                            className="text-danger fs-12"
                                                            style={{
                                                                display: `${otpExpired ? 'block' : 'none'}`,
                                                            }}
                                                        >
                                                            OTP has expired.
                                                        </small>
                                                    )}
                                                </div>
                                                {!hideAfterVerifiedText && !showOTPBtn && (
                                                    <button
                                                        onClick={() => {
                                                            if (!disableResetButton) {
                                                                setDisableResetButton(true);
                                                                setTimer(30);
                                                                if (
                                                                    candidateStatus !== undefined &&
                                                                    candidateStatus === CandidateStatus.Uploaded && // assuming `CandidateStatus.Joined === 1`
                                                                    candidateId
                                                                ) {
                                                                    fetchData(
                                                                        values.email!,
                                                                        OTPType.UnclaimedAccount,
                                                                        candidateId
                                                                    );
                                                                } else {
                                                                    fetchData(values.email!, otpType);
                                                                }

                                                                setFieldValue('otp', '');
                                                                setConcatenatedOtp('');
                                                            }
                                                            resetOtp();
                                                        }}
                                                        type="button"
                                                        disabled={disableResetButton}
                                                        className="fs-12 text-decoration-underline lt-text-primary-alt cursor-pointer"
                                                    >
                                                        Resend OTP{' '}
                                                        {disableResetButton ? (
                                                            timer === 0 ? (
                                                                <>00</>
                                                            ) : (
                                                                'in ' + timer + ' sec'
                                                            )
                                                        ) : (
                                                            ''
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            {!showOTPBtn ? (
                                                otpVerified ? (
                                                    <button
                                                        className={
                                                            'btn btn-primary px-4 ' +
                                                            (useLargeButtons ? 'w-100 h-44' : '')
                                                        }
                                                        type="button"
                                                        onClick={() => {
                                                            if (
                                                                (!hasUser?.accountLinkedStatus &&
                                                                    hasUser?.authType !== SocialEnum.CustomAuth) ||
                                                                (!hasHRMSUser?.accountLinkedStatus &&
                                                                    hasHRMSUser?.authType !==
                                                                        (SocialEnum.CustomAuth || SocialEnum.hrms)) ||
                                                                ConfirmCandidateInvitationId ||
                                                                ConfirmManagerInvitationId
                                                            ) {
                                                                setLoadingSpin?.(true);
                                                                resetForm();
                                                                setTimeout(() => {
                                                                    setShowPasswordTab?.(true);
                                                                    setLoadingSpin?.(false);
                                                                }, 1000);
                                                            } else {
                                                                setLoadingSpin?.(true);
                                                                setMigrateFromEmailOnly?.(true);
                                                                setLoadingSpin?.(false);
                                                            }
                                                        }}
                                                    >
                                                        {(!hasUser?.accountLinkedStatus &&
                                                            hasUser?.authType === SocialEnum.CustomAuth) ||
                                                        (!hasHRMSUser?.accountLinkedStatus &&
                                                            hasHRMSUser?.authType ===
                                                                (SocialEnum.CustomAuth || SocialEnum.hrms)) ||
                                                        ConfirmCandidateInvitationId ||
                                                        ConfirmManagerInvitationId
                                                            ? 'Continue'
                                                            : 'Continue'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        disabled={concatenatedOtp.length === 6 ? false : true}
                                                        className={
                                                            'btn btn-primary px-4 ' +
                                                            (useLargeButtons ? 'w-100 h-44' : '')
                                                        }
                                                        style={{ display: `${otpVerified ? 'none' : 'block'}` }}
                                                        type="button"
                                                        onClick={() => {
                                                            if (values.email) {
                                                                localStorageSetItem('Email', values.email);
                                                                if (candidateType) {
                                                                    validateOtp({
                                                                        email: values.email,
                                                                        otpType: affilateReferral
                                                                            ? OTPType.AffiliateMarketing
                                                                            : otpType!,
                                                                        otp: concatenatedOtp,
                                                                        accountType: candidateType,
                                                                        ipAddress: policy.countryData?.ipAddress!,
                                                                    });
                                                                } else {
                                                                    validateOtp({
                                                                        email: values.email,
                                                                        otpType: affilateReferral
                                                                            ? OTPType.AffiliateMarketing
                                                                            : otpType!,
                                                                        otp: concatenatedOtp,
                                                                        ipAddress: policy.countryData?.ipAddress!,
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        Verify
                                                    </button>
                                                )
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </>
    );
};

export default NewRegOTP;

const getValidationSchema = () =>
    objectYup().shape({
        email: stringYup().required('Email is required.').matches(emailRegexp, {
            message: 'Invalid email format.',
        }),
        password: stringYup()
            .required('Password is required.')
            .min(8, 'Password must be in between 8 - 16 characters.')
            .matches(passwordRegExp, 'Password must match the below conditions.'),
        cpassword: stringYup()
            .nullable()
            .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
            .required('Confirm password is required.'),
    });
